import { ConflictException, Injectable, NotFoundException } from '@nestjs/common';
import {
  ChatSession,
  Prisma,
  ServiceRequest,
  ServiceRequestCategory,
  ServiceRequestEvent,
  ServiceRequestNote,
  ServiceRequestPriority,
  ServiceRequestStatus,
} from '@chat/prisma-client';
import { PrismaService } from '../prisma/prisma.service';

export interface Actor {
  id: string;
  name: string;
}

export type ServiceRequestScope = 'mine' | 'open' | 'all';

export interface ListServiceRequestsQuery {
  scope: ServiceRequestScope;
  status?: ServiceRequestStatus;
  priority?: ServiceRequestPriority;
  category?: ServiceRequestCategory;
  search?: string;
}

// Linear per the spec's workflow diagram — no skipping stages. "closed" can
// only be reached from "in_review", and can only be left via reopen() (a
// distinct action, not a generic status PATCH target from anywhere else).
const ALLOWED_TRANSITIONS: Record<ServiceRequestStatus, ServiceRequestStatus[]> = {
  open: ['assigned'],
  assigned: ['in_progress'],
  in_progress: ['in_review'],
  in_review: ['closed'],
  closed: [],
  reopened: [],
};

@Injectable()
export class ServiceRequestService {
  constructor(private readonly prisma: PrismaService) {}

  // Called from ChatService.markEscalated() only on a session's first-ever
  // escalation (subsequent switches back to agent reuse this same SR — see
  // that method's comment; sessionId is unique here, so a second SR for the
  // same session is impossible by construction). Enforces "one active SR
  // per customer per application" across *different* sessions — active
  // meaning any status other than closed (a reopened SR immediately becomes
  // 'open' again, so 'closed' is the only resting inactive status — see
  // reopen()). Skipped for anonymous shoppers (no externalUserId): there's
  // no stable identity to check across sessions for them, and within a
  // single session the unique FK already caps them at one SR regardless.
  async createForEscalation(session: ChatSession): Promise<ServiceRequest> {
    if (session.externalUserId) {
      const active = await this.prisma.serviceRequest.findFirst({
        where: {
          applicationId: session.applicationId,
          externalUserId: session.externalUserId,
          status: { not: 'closed' },
        },
      });

      if (active) {
        throw new ConflictException(
          `You already have an open support request (SR-${active.srNumber}). It must be closed before a new one can be started.`,
        );
      }
    }

    const serviceRequest = await this.prisma.serviceRequest.create({
      data: {
        applicationId: session.applicationId,
        sessionId: session.id,
        externalUserId: session.externalUserId,
        externalUserName: session.externalUserName,
        externalUserEmail: session.externalUserEmail,
      },
    });

    await this.logEvent(serviceRequest.id, { type: 'created' });

    return serviceRequest;
  }

  async list(applicationId: string | null, agentId: string, query: ListServiceRequestsQuery): Promise<ServiceRequest[]> {
    const where: Prisma.ServiceRequestWhereInput = {
      ...(applicationId ? { applicationId } : {}),
      ...(query.scope === 'mine' ? { assignedAgentId: agentId } : {}),
      ...(query.scope === 'open' ? { status: 'open' } : {}),
      ...(query.status ? { status: query.status } : {}),
      ...(query.priority ? { priority: query.priority } : {}),
      ...(query.category ? { category: query.category } : {}),
      ...(query.search
        ? {
            OR: [
              { externalUserName: { contains: query.search, mode: 'insensitive' } },
              { externalUserEmail: { contains: query.search, mode: 'insensitive' } },
            ],
          }
        : {}),
    };

    return this.prisma.serviceRequest.findMany({
      where,
      orderBy: { createdOn: 'desc' },
      include: {
        session: { include: { messages: { orderBy: { createdOn: 'desc' }, take: 1 } } },
        // Which store this SR belongs to — a platform-wide agent (applicationId:
        // null above) sees SRs across every store's application, so the admin
        // UI needs this to label/link each one back to its actual store.
        application: { select: { name: true, slug: true, externalStoreId: true } },
      },
    });
  }

  async getForAgentOrThrow(applicationId: string | null, id: string): Promise<ServiceRequest> {
    const serviceRequest = await this.prisma.serviceRequest.findUnique({
      where: { id },
      include: {
        session: true,
        application: { select: { name: true, slug: true, externalStoreId: true } },
      },
    });

    if (!serviceRequest || (applicationId !== null && serviceRequest.applicationId !== applicationId)) {
      throw new NotFoundException('Service request not found');
    }

    return serviceRequest;
  }

  async updateStatus(
    applicationId: string | null,
    id: string,
    toStatus: ServiceRequestStatus,
    actor: Actor,
  ): Promise<ServiceRequest> {
    const current = await this.getForAgentOrThrow(applicationId, id);

    if (!ALLOWED_TRANSITIONS[current.status].includes(toStatus)) {
      throw new ConflictException(`Cannot move a Service Request from "${current.status}" to "${toStatus}"`);
    }

    const updated = await this.prisma.serviceRequest.update({
      where: { id },
      data: { status: toStatus, ...(toStatus === 'closed' ? { closedAt: new Date() } : {}) },
    });

    await this.logEvent(id, {
      type: toStatus === 'closed' ? 'closed' : 'status_changed',
      fromValue: current.status,
      toValue: toStatus,
      actor,
    });

    // Closing the ticket closes the conversation thread too — otherwise a
    // customer messaging a "closed" ticket was silently absorbed into a
    // session with no ticket to show for it (the bug this cascade fixes).
    // "Closed tickets will not open" for the customer starts here.
    if (toStatus === 'closed') {
      await this.prisma.chatSession.update({
        where: { id: current.sessionId },
        data: { status: 'closed', closedAt: new Date() },
      });
    }

    return updated;
  }

  // Reopening is its own action, not a generic status PATCH target — a
  // closed SR goes straight back to "open" (not a persistent "reopened"
  // resting state), logged as a distinct timeline event type.
  async reopen(applicationId: string | null, id: string, actor: Actor): Promise<ServiceRequest> {
    const current = await this.getForAgentOrThrow(applicationId, id);

    if (current.status !== 'closed') {
      throw new ConflictException('Only a closed Service Request can be reopened');
    }

    const updated = await this.prisma.serviceRequest.update({
      where: { id },
      data: { status: 'open', closedAt: null },
    });

    await this.logEvent(id, { type: 'reopened', fromValue: 'closed', toValue: 'open', actor });

    // Mirrors updateStatus's close cascade — a closed SR always means a
    // closed session (see the comment there), so reopening the ticket must
    // reopen the session too, or it stays unreachable from the customer side.
    await this.prisma.chatSession.update({
      where: { id: current.sessionId },
      data: { status: 'open', closedAt: null },
    });

    return updated;
  }

  async assign(applicationId: string | null, id: string, agentId: string, agentName: string, actor: Actor): Promise<ServiceRequest> {
    const current = await this.getForAgentOrThrow(applicationId, id);

    const updated = await this.prisma.serviceRequest.update({
      where: { id },
      data: {
        assignedAgentId: agentId,
        assignedAgentName: agentName,
        ...(current.status === 'open' ? { status: 'assigned' } : {}),
      },
    });

    await this.logEvent(id, {
      type: 'assigned',
      fromValue: current.assignedAgentName ?? undefined,
      toValue: agentName,
      actor,
    });

    return updated;
  }

  async transfer(applicationId: string | null, id: string, agentId: string, agentName: string, actor: Actor): Promise<ServiceRequest> {
    const current = await this.getForAgentOrThrow(applicationId, id);

    const updated = await this.prisma.serviceRequest.update({
      where: { id },
      data: { assignedAgentId: agentId, assignedAgentName: agentName },
    });

    await this.logEvent(id, {
      type: 'transferred',
      fromValue: current.assignedAgentName ?? undefined,
      toValue: agentName,
      actor,
    });

    return updated;
  }

  async listNotes(applicationId: string | null, id: string): Promise<ServiceRequestNote[]> {
    await this.getForAgentOrThrow(applicationId, id);

    return this.prisma.serviceRequestNote.findMany({
      where: { serviceRequestId: id },
      orderBy: { createdOn: 'asc' },
    });
  }

  async addNote(applicationId: string | null, id: string, content: string, actor: Actor): Promise<ServiceRequestNote> {
    await this.getForAgentOrThrow(applicationId, id);

    const note = await this.prisma.serviceRequestNote.create({
      data: { serviceRequestId: id, authorId: actor.id, authorName: actor.name, content },
    });

    await this.logEvent(id, { type: 'note_added', actor });

    return note;
  }

  async timeline(applicationId: string | null, id: string): Promise<ServiceRequestEvent[]> {
    await this.getForAgentOrThrow(applicationId, id);

    return this.prisma.serviceRequestEvent.findMany({
      where: { serviceRequestId: id },
      orderBy: { createdOn: 'asc' },
    });
  }

  private async logEvent(
    serviceRequestId: string,
    entry: { type: ServiceRequestEvent['type']; fromValue?: string; toValue?: string; actor?: Actor },
  ): Promise<void> {
    await this.prisma.serviceRequestEvent.create({
      data: {
        serviceRequestId,
        type: entry.type,
        fromValue: entry.fromValue,
        toValue: entry.toValue,
        actorId: entry.actor?.id,
        actorName: entry.actor?.name,
      },
    });
  }
}
